/**
 * DriverCommPrefs — driver self-serve communication preferences (consent).
 * GET/PUT /me/consent. Lets a driver opt in/out of SMS + email. Opted-out
 * drivers are automatically excluded from bulk campaigns. No protected logic.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { Switch } from "../ui/switch";
import { toast } from "sonner";
import { Bell, MessageSquare, Mail, Loader2 } from "lucide-react";

export const DriverCommPrefs = () => {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchPrefs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/me/consent");
      setPrefs(res.data);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load preferences");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const update = async (key, value) => {
    setSaving(true);
    // optimistic
    setPrefs((p) => ({ ...p, [key]: value }));
    try {
      const res = await api.put("/me/consent", { [key]: value });
      setPrefs(res.data);
      toast.success("Preferences updated.");
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't update preferences");
      fetchPrefs();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="driver-comm-prefs">
      <div>
        <h3 className="font-heading text-lg font-black tracking-tight flex items-center gap-2">
          <Bell className="w-4 h-4" /> Communication preferences
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Choose how dispatch can reach you with fleet updates. Ride-critical messages always send.
        </p>
      </div>
      {loading ? (
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-2">
          <label className="flex items-center justify-between gap-3 border border-border p-3" data-testid="prefs-sms-row">
            <span className="inline-flex items-center gap-2 text-sm font-medium">
              <MessageSquare className="w-4 h-4" /> SMS updates
            </span>
            <Switch
              checked={!!prefs?.sms_consent}
              disabled={saving}
              onCheckedChange={(v) => update("sms_consent", v)}
              data-testid="prefs-sms-switch"
            />
          </label>
          <label className="flex items-center justify-between gap-3 border border-border p-3" data-testid="prefs-email-row">
            <span className="inline-flex items-center gap-2 text-sm font-medium">
              <Mail className="w-4 h-4" /> Email updates
            </span>
            <Switch
              checked={!!prefs?.email_consent}
              disabled={saving}
              onCheckedChange={(v) => update("email_consent", v)}
              data-testid="prefs-email-switch"
            />
          </label>
        </div>
      )}
    </div>
  );
};

export default DriverCommPrefs;
